import type {ReactNode} from "react";
import {useTranslation} from "react-i18next";
import type {CabinetSourceError, OrderState, PaymentItem} from "./model";

export {formatDate, formatMinor} from "../../shared/format";

export function PageHeading({eyebrow, title, lead, action}: {eyebrow: string; title: string; lead: string; action?: ReactNode}) {
  return <header className="cabinet-page-heading">
    <div><p className="eyebrow">{eyebrow}</p><h1>{title}</h1><p>{lead}</p></div>
    {action}
  </header>;
}

export function SectionLoader({label}: {label: string}) {
  return <div className="cabinet-state compact" aria-live="polite"><span className="page-loader" aria-hidden="true" /><span>{label}</span></div>;
}

export function SourceUnavailable({onRetry, requestId}: {onRetry: () => void; requestId?: string}) {
  const {t} = useTranslation();
  return <div className="cabinet-state compact" role="alert">
    <span className="state-icon" aria-hidden="true">!</span>
    <strong>{t("cabinet.states.unavailableTitle")}</strong>
    <p>{t("cabinet.states.unavailableBody")}</p>
    {requestId && <small>{t("common.requestId")}: <code>{requestId}</code></small>}
    <button className="button secondary" type="button" onClick={onRetry}>{t("common.retry")}</button>
  </div>;
}

export function EmptyState({title, body, children}: {title: string; body: string; children?: ReactNode}) {
  return <div className="cabinet-state"><span className="state-icon" aria-hidden="true">◇</span><h2>{title}</h2><p>{body}</p>{children}</div>;
}

export function requestIdOf(error: unknown): string | undefined {
  return error && typeof error === "object" && "requestId" in error ? String((error as CabinetSourceError).requestId || "") || undefined : undefined;
}

export function OrderStateBadge({state}: {state: OrderState}) {
  const {t} = useTranslation();
  return <span className={`cabinet-badge order-${state}`}>{t(`cabinet.orders.states.${state}`)}</span>;
}

export function PaymentStateBadge({state}: {state: PaymentItem["state"]}) {
  const {t} = useTranslation();
  return <span className={`cabinet-badge payment-${state}`}>{t(`cabinet.wallet.paymentStates.${state}`)}</span>;
}
